import React, { useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import moment from 'moment';
import { useTranslation, getLocale, formatNumber, layoutLtrStyle } from '../../shared/i18n';
import { toAsciiDateKey } from '../../shared/utils/apiParams';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable2 } from '../../components/common/Clickable2';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT } from '../../shared/theme/sharedStyles';
import { HorizontalLtrScrollView } from '../../components/ui/HorizontalLtrScrollView';

const ITEM_WIDTH = 44;
const ITEM_SPACING = 6;
const FULL_ITEM_WIDTH = ITEM_WIDTH + ITEM_SPACING;

function applyMomentLocale(locale: string): 'ar' | 'en' {
  const loc = locale === 'ar' ? 'ar' : 'en';
  try {
    moment.locale(loc);
  } catch {
    moment.locale('en');
  }
  return loc;
}

function createMenuFlowDateSliderStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      zIndex: 1000,
      // Safe-area clearance is owned by header chrome / DATE_BLOCK height — do not pad insets here.
      height: MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT,
      backgroundColor: 'transparent',
      justifyContent: 'flex-end',
    },
    monthRow: {
      // Chronological chrome: physical left = prev, right = next — never mirror in RTL.
      ...layoutLtrStyle,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      marginBottom: 4,
    },
    navButton: {
      width: 32,
      height: 32,
      minWidth: 44,
      minHeight: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    monthNameWrap: {
      alignSelf: 'center',
      borderBottomWidth: 1,
      borderBottomColor: c.surface,
      paddingBottom: 1,
    },
    monthName: {
      fontSize: 16,
      fontWeight: '600',
      color: c.surface,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    dateListWrap: {
      ...layoutLtrStyle,
      height: 60,
    },
    dateList: {
      ...layoutLtrStyle,
      flexDirection: 'row',
      paddingHorizontal: 16,
      alignItems: 'center',
      height: 60,
    },
    dateItemWrap: {
      width: ITEM_WIDTH,
      marginHorizontal: ITEM_SPACING,
    },
    dateItemContent: {
      width: ITEM_WIDTH,
      height: 44,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 12,
      borderBottomWidth: 3,
      borderBottomColor: 'transparent',
      opacity: 0.8,
    },
    dateItemContentInner: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
      minWidth: ITEM_WIDTH,
    },
    dateItemContentSelected: {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottomColor: c.surface,
      borderTopColor: c.surface,
      borderTopWidth: 0,
      borderLeftColor: c.surface,
      borderLeftWidth: 0,
      borderRightColor: c.surface,
      borderRightWidth: 0,
      backgroundColor: 'rgba(255,255,255,0)',
      opacity: 1,
    },
    dateItemContentForced: {
      backgroundColor: c.greyLight,
      borderBottomColor: c.greyLight,
    },
    dayName: {
      fontSize: 8,
      fontWeight: '600',
      color: c.surface,
      marginBottom: 2,
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    dayNameSelected: { fontSize: 10, color: c.surface },
    dayNameDisabled: { color: 'rgba(255,255,255,0.3)' },
    dayNameForced: { color: 'rgba(255,255,255,0.3)' },
    dayNumber: {
      fontSize: 16,
      fontWeight: '600',
      color: c.surface,
      textAlign: 'center',
    },
    dayNumberSelected: {
      fontSize: 18,
      color: c.surface,
      fontWeight: '800',
    },
    dayNumberDisabled: { color: 'rgba(255,255,255,0.3)' },
    dayNumberForced: { color: 'rgba(255,255,255,0.3)' },
  });
}

interface MenuFlowDateItemProps {
  date: string;
  dayName: string;
  dayNumber: string;
  isSelected: boolean;
  isDisabled: boolean;
  isForcedDisabled: boolean;
  onPress: (date: string) => void;
  styles: ReturnType<typeof createMenuFlowDateSliderStyles>;
}

interface ProcessedDateItem {
  date: string;
  dayName: string;
  dayNumber: string;
  isSelectable: boolean;
  isForced: boolean;
}

export interface MenuFlowDateSliderProps {
  availableDates: string[];
  selectableDates?: string[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  dayConfigMap?: Record<string, { forcedDisabled?: boolean }>;
  forcedDisabledDates?: string[];
}

const MenuFlowDateItem = memo(({ date, dayName, dayNumber, isSelected, isDisabled, isForcedDisabled, onPress, styles }: MenuFlowDateItemProps) => {
  const is_selected_or_forced = isSelected || isForcedDisabled;
  const is_disabled = isDisabled && !isForcedDisabled;

  return (
    <View style={styles.dateItemWrap}>
      <Clickable2
        onPress={() => onPress(date)}
        disabled={isDisabled || isForcedDisabled}
        style={[
          styles.dateItemContent,
          isForcedDisabled && styles.dateItemContentForced,
          isSelected && !isForcedDisabled && styles.dateItemContentSelected,
          { backgroundColor: 'transparent', minWidth: ITEM_WIDTH, width: ITEM_WIDTH, height: 44, padding: 0 },
        ]}
        accessibilityLabel={`${dayName} ${dayNumber}`}
        accessibilityRole="button"
      >
        <View style={styles.dateItemContentInner}>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={[
              styles.dayName,
              is_selected_or_forced && !isForcedDisabled && styles.dayNameSelected,
              isForcedDisabled && styles.dayNameForced,
              is_disabled && styles.dayNameDisabled,
            ]}>
            {dayName}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={[
              styles.dayNumber,
              is_selected_or_forced && !isForcedDisabled && styles.dayNumberSelected,
              isForcedDisabled && styles.dayNumberForced,
              is_disabled && styles.dayNumberDisabled,
            ]}>
            {dayNumber}
          </Text>
        </View>
      </Clickable2>
    </View>
  );
});

export const MenuFlowDateSlider: React.FC<MenuFlowDateSliderProps> = ({
  availableDates = [],
  selectableDates,
  selectedDate,
  onDateSelect,
  onNavigatePrev,
  onNavigateNext,
  dayConfigMap = {},
  forcedDisabledDates = [],
}) => {
  const scroll_view_ref = useRef<ScrollView>(null);
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowDateSliderStyles(colors), [colors]);

  const locale = getLocale();
  const selectable_ascii = useMemo(
    () => new Set((selectableDates ?? []).map(d => toAsciiDateKey(d)).filter(Boolean)),
    [selectableDates],
  );
  const forced_ascii = useMemo(
    () => new Set(forcedDisabledDates.map(d => toAsciiDateKey(d)).filter(Boolean)),
    [forcedDisabledDates],
  );
  const selected_ascii = toAsciiDateKey(selectedDate);

  const processedDates = useMemo((): ProcessedDateItem[] => {
    applyMomentLocale(locale);
    return availableDates.map((d: string) => {
      const date_ascii = toAsciiDateKey(d);
      const m = moment(date_ascii);
      const config = dayConfigMap[date_ascii] ?? dayConfigMap[d];
      return {
        date: date_ascii,
        dayName: m.format('ddd'),
        dayNumber: formatNumber(m.date()),
        isSelectable: selectableDates ? selectable_ascii.has(date_ascii) : !!config,
        isForced: !!config?.forcedDisabled || forced_ascii.has(date_ascii),
      };
    });
  }, [availableDates, selectableDates, selectable_ascii, dayConfigMap, forced_ascii, locale]);

  useEffect(() => {
    const idx = processedDates.findIndex(row => row.date === selected_ascii);
    if (idx !== -1 && scroll_view_ref.current) {
      const scroll_x = idx * FULL_ITEM_WIDTH - ITEM_WIDTH * 2;
      const timer = setTimeout(() => {
        scroll_view_ref.current?.scrollTo({
          x: Math.max(0, scroll_x),
          animated: true,
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selected_ascii, processedDates]);

  const handleStep = useCallback(
    (direction: 'prev' | 'next') => {
      const currentIndex = processedDates.findIndex(row => row.date === selected_ascii);
      if (currentIndex === -1) {
        return;
      }
      const isNext = direction === 'next';
      const step = isNext ? 1 : -1;
      for (let i = currentIndex + step; isNext ? i < processedDates.length : i >= 0; i += step) {
        const row = processedDates[i];
        if (row.isSelectable && !row.isForced) {
          onDateSelect(row.date);
          break;
        }
      }
    },
    [selected_ascii, processedDates, onDateSelect],
  );

  const currentMonthLabel = useMemo(() => {
    if (!selected_ascii) {
      return '';
    }
    applyMomentLocale(locale);
    return moment(selected_ascii).format('MMMM');
  }, [selected_ascii, locale]);

  return (
    <View style={styles.container}>
      <View style={styles.monthRow}>
        <Clickable2
          onPress={() => (onNavigatePrev ? onNavigatePrev() : handleStep('prev'))}
          style={styles.navButton}
          accessibilityLabel={t('menu.previousDates')}
          accessibilityRole="button"
        >
          <View>
            <SvgIcon name="arrow-left" size={22} color={colors.surface} />
          </View>
        </Clickable2>
        <View style={styles.monthNameWrap}>
          <Text
            allowFontScaling={false}
            style={[styles.monthName, { writingDirection: locale === 'ar' ? 'rtl' : 'ltr' }]}>
            {currentMonthLabel}
          </Text>
        </View>
        <Clickable2
          onPress={() => (onNavigateNext ? onNavigateNext() : handleStep('next'))}
          style={styles.navButton}
          accessibilityLabel={t('menu.nextDates')}
          accessibilityRole="button"
        >
          <View>
            <SvgIcon name="arrow-right" size={22} color={colors.surface} />
          </View>
        </Clickable2>
      </View>
      <View style={styles.dateListWrap}>
        <HorizontalLtrScrollView
          ref={scroll_view_ref}
          horizontal
          directionalLockEnabled
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.dateList}
          snapToInterval={FULL_ITEM_WIDTH}
          decelerationRate="fast"
          removeClippedSubviews={false}
          scrollEventThrottle={32}
          disableIntervalMomentum={true}>
          {processedDates.map(item => (
            <MenuFlowDateItem
              key={item.date}
              date={item.date}
              dayName={item.dayName}
              dayNumber={item.dayNumber}
              isSelected={item.date === selected_ascii}
              isDisabled={!item.isSelectable}
              isForcedDisabled={item.isForced}
              onPress={onDateSelect}
              styles={styles}
            />
          ))}
        </HorizontalLtrScrollView>
      </View>
    </View>
  );
};

export default memo(MenuFlowDateSlider);
